/**
 * useVqeUndoShortcut — window-level Mod+Z / Mod+Shift+Z for the Query Editor's
 * Visual view, whose Monaco stays mounted but hidden, so nothing focused
 * answers the shortcut on its own. `step` drives that Monaco model's undo
 * stack, which keeps typed and visual edits in one order.
 *
 * A window listener rather than `onKeyDown` on the root: focus may rest on
 * `document.body` right after the view opens. Guards — all must pass, or the
 * key is left alone (never `preventDefault`ed):
 *  - `active` — only the focused tab's view acts; a background tab never steals it.
 *  - not typing into an input / textarea / contenteditable / Monaco — those own
 *    Cmd/Ctrl+Z for their own history.
 *  - `step` stepped something — an empty stack is a genuine no-op.
 *
 * Fires `feature:vqe_undo { direction, via: 'keyboard', surface: 'mode' }` on
 * success only.
 */
import { useEffect, useRef } from 'react';

import { useAnalytics } from '../../../../core/analytics/useAnalytics';

/** Typing targets own Cmd/Ctrl+Z (and "?") for themselves. */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  const tag = el?.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    el?.isContentEditable === true ||
    el?.closest?.('.monaco-editor') != null
  );
}

export type UseVqeUndoShortcutArgs = {
  readonly active: boolean;
  /** Step the history; false when there was nothing to step (the key is left alone). */
  readonly step: (direction: 'undo' | 'redo') => boolean;
};

export function useVqeUndoShortcut({ active, step }: UseVqeUndoShortcutArgs): void {
  const { track } = useAnalytics();
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    if (!active) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      // Mod = metaKey on darwin, ctrlKey elsewhere — accepting EITHER matches
      // how the rest of this codebase detects the modifier.
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
      if (event.defaultPrevented || isTextEntryTarget(event.target)) return;

      const direction: 'undo' | 'redo' = event.shiftKey ? 'redo' : 'undo';
      if (!stepRef.current(direction)) return;

      event.preventDefault();
      track('feature:vqe_undo', { direction, via: 'keyboard', surface: 'mode' });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, track]);
}
